/**
 * AdoraCare - CLINIC LINKS view model
 * (DEEPAK-FRONT-OFFICE-FEEDBACK-CORRECTIVE-V1, item 2).
 *
 * Pure, React-free helpers + copy for the front-desk review / social link bar.
 * Kept side-effect-free (no React, no CSS, no api import) so the vitest suite
 * exercises every helper by direct call and renders the presentational
 * component via react-dom/server (this repo has NO jsdom).
 *
 * THE CONTRACT (config-present / config-absent):
 *   - The links are ORG CONFIG resolved server-side through THE ONE
 *     org-workflow-settings reader (`clinic_links.google_review_url` /
 *     `clinic_links.instagram_url`) and delivered on the /me payload as
 *     `me.clinicLinks`. Each entry is a SERVER-VALIDATED https URL.
 *   - A link the org has not configured is ABSENT from the payload, and this
 *     module returns NO entry for it -- the surface renders nothing, never a
 *     disabled or placeholder button (no dead doors, LES-016).
 *   - With no links at all, `buildClinicLinkEntries` returns [] and the hosting
 *     surface renders NOTHING (not an empty shell, not a "coming" state).
 *
 * SCOPE LINE: open-in-new-tab + copy-link are the ONLY actions. Nothing here
 * messages a patient -- the send-to-patient variant is a NAMED DEFERRAL under
 * the two-channel rule (CLAUDE.md). These are the clinic's own public
 * marketing URLs: zero PHI, zero authority, no audit event.
 */

import type { DashboardMe } from '@/dashboard/types';

// --- Copy (single source; asserted in tests) --------------------------------

export const CLINIC_LINKS_TITLE = 'Ask for a review';
export const CLINIC_LINKS_HINT =
  'Open the page for the patient, or copy the link to hand over.';
export const CLINIC_LINK_COPY_ACTION = 'Copy link';
export const CLINIC_LINK_COPIED = 'Link copied';

/** The stable ids the tests + copy handler key on. */
export type ClinicLinkId = 'google_review' | 'instagram';

export interface ClinicLinkEntry {
  readonly id: ClinicLinkId;
  /** The operator-facing label for the open action. */
  readonly openLabel: string;
  /** The server-validated https URL. */
  readonly href: string;
}

/** Open-action labels, in render order. Product vocabulary, stated once. */
export const CLINIC_LINK_OPEN_LABEL: Readonly<Record<ClinicLinkId, string>> =
  Object.freeze({
    google_review: 'Open Google review page',
    instagram: 'Open Instagram',
  });

/**
 * Project the /me payload into the ordered render list. Google review first
 * (the tenant's primary front-desk ask), Instagram second. An absent or blank
 * entry is DROPPED, never rendered disabled. Total + pure.
 *
 * Defence in depth: the server already validated each URL, but a payload from
 * an older/partial backend is untrusted input to this module, so a value that
 * is not a non-empty https string is dropped here too (a config value can
 * never reach an href unvalidated on either side of the wire).
 */
export function buildClinicLinkEntries(
  me: DashboardMe | null,
): readonly ClinicLinkEntry[] {
  const links = me?.clinicLinks;
  if (links === undefined || links === null) return [];
  const entries: ClinicLinkEntry[] = [];
  const google = safeHttpsHref(links.googleReviewUrl);
  if (google !== null) {
    entries.push({
      id: 'google_review',
      openLabel: CLINIC_LINK_OPEN_LABEL.google_review,
      href: google,
    });
  }
  const instagram = safeHttpsHref(links.instagramUrl);
  if (instagram !== null) {
    entries.push({
      id: 'instagram',
      openLabel: CLINIC_LINK_OPEN_LABEL.instagram,
      href: instagram,
    });
  }
  return entries;
}

/** A non-empty https string, or null. The frontend half of the ONE rule. */
export function safeHttpsHref(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  return /^https:\/\/[^\s]+$/i.test(trimmed) ? trimmed : null;
}
